/**
 * One shared `IntersectionObserver` per option set, used by every reveal/counter/scrub primitive
 * (DESIGN_SYSTEM §3): dozens of observed elements cost one observer, and "reveal once" is handled in
 * one place. Fails **open**: with no IntersectionObserver (old browser, SSR, jsdom) the callback
 * fires immediately, so content is never stuck invisible.
 */

export interface ObserveOptions {
  /** Default `0px 0px -20% 0px`: an element counts as visible once its top is in the top 80 % of the viewport. */
  rootMargin?: string;
  threshold?: number;
  /** Stop observing after the first intersection. Default true. */
  once?: boolean;
  /** Also call back when the element leaves (`entry.isIntersecting === false`). Default false. */
  reportExit?: boolean;
}

export type ObserveCallback = (entry: IntersectionObserverEntry | null) => void;

export const DEFAULT_ROOT_MARGIN = '0px 0px -20% 0px';

interface Registration {
  callback: ObserveCallback;
  once: boolean;
  reportExit: boolean;
}

interface Shared {
  observer: IntersectionObserver;
  registrations: Map<Element, Registration[]>;
}

const shared = new Map<string, Shared>();

function sharedFor(rootMargin: string, threshold: number): Shared {
  const key = `${rootMargin}|${String(threshold)}`;
  const existing = shared.get(key);
  if (existing) return existing;

  const registrations = new Map<Element, Registration[]>();
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const list = registrations.get(entry.target);
        if (!list) continue;
        for (const registration of [...list]) {
          if (!entry.isIntersecting) {
            if (registration.reportExit) registration.callback(entry);
            continue;
          }
          registration.callback(entry);
          if (registration.once) remove(key, entry.target, registration);
        }
      }
    },
    { rootMargin, threshold },
  );
  const created: Shared = { observer, registrations };
  shared.set(key, created);
  return created;
}

function remove(key: string, element: Element, registration: Registration): void {
  const group = shared.get(key);
  if (!group) return;
  const list = group.registrations.get(element);
  if (!list) return;
  const remaining = list.filter((item) => item !== registration);
  if (remaining.length > 0) {
    group.registrations.set(element, remaining);
    return;
  }
  group.registrations.delete(element);
  group.observer.unobserve(element);
  if (group.registrations.size === 0) {
    group.observer.disconnect();
    shared.delete(key);
  }
}

/** Observe `element`; returns the unsubscribe function. */
export function observe(
  element: Element,
  callback: ObserveCallback,
  {
    rootMargin = DEFAULT_ROOT_MARGIN,
    threshold = 0,
    once = true,
    reportExit = false,
  }: ObserveOptions = {},
): () => void {
  if (typeof IntersectionObserver === 'undefined') {
    callback(null);
    return () => undefined;
  }
  const key = `${rootMargin}|${String(threshold)}`;
  const group = sharedFor(rootMargin, threshold);
  const registration: Registration = { callback, once, reportExit };
  const list = group.registrations.get(element) ?? [];
  list.push(registration);
  group.registrations.set(element, list);
  if (list.length === 1) group.observer.observe(element);
  return () => {
    remove(key, element, registration);
  };
}

/** Test helper: how many observers exist right now. */
export function observerCount(): number {
  return shared.size;
}

/** Test helper: drop every shared observer (module state must not leak between tests). */
export function resetObservers(): void {
  for (const group of shared.values()) group.observer.disconnect();
  shared.clear();
}
